import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminUser, AuthSession, AvailableProject } from './types';
import { writeLastUser } from './lastUser';
import { resolveActiveProjectId } from './projectSelection';

/**
 * K-8 (#24) — refresh token bu store'da (ve localStorage'da) TUTULMAZ.
 * Taşıyıcı, BE'nin login/refresh'te set ettiği httpOnly `emlak_refresh_token`
 * cookie'sidir; yenileme `withCredentials` ile boş gövdeli POST yapar
 * (bkz. `refreshSessionFromCookie`, src/api/http.ts). Access token mevcut
 * yerinde (persist edilen state) kalır.
 *
 * REQ-45 / K-3 — çoklu-site: `availableProjects` + `defaultProjectId` login/
 * refresh yanıtından gelir; `activeProjectId` (persist) her admin isteğine
 * `X-Project-Id` olarak enjekte edilir (src/api/http.ts). Seçim her zaman
 * erişilebilir bir projeye normalize edilir (bkz. `resolveActiveProjectId`).
 */
interface AuthState {
  user: AdminUser | null;
  accessToken: string | null;
  expiresAt: string | null;
  isHydrated: boolean;

  // REQ-45 — çoklu-site proje bağlamı.
  availableProjects: AvailableProject[];
  defaultProjectId: string | null;
  activeProjectId: string | null;

  setSession: (session: AuthSession) => void;
  setUser: (user: AdminUser) => void;
  updateTokens: (accessToken: string, expiresAt: string) => void;
  clearSession: () => void;
  setHydrated: () => void;

  /** Switcher seçimi — yalnız erişilebilir bir projeye izin verir. */
  setActiveProject: (projectId: string) => void;
  /** PROJECT_ACCESS_DENIED / geçersiz seçim → güvenli (varsayılan) projeye dön. */
  resetActiveProject: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      expiresAt: null,
      isHydrated: false,
      availableProjects: [],
      defaultProjectId: null,
      activeProjectId: null,
      setSession: (session) => {
        writeLastUser(session.user);
        set((state) => {
          // Login/refresh/change-password proje listesini taşır. Bir auth sonucu
          // (savunmacı) proje taşımıyorsa mevcut bağlamı KORU — boş liste yüzünden
          // aktif proje sıfırlanmasın.
          const hasProjects = (session.availableProjects?.length ?? 0) > 0;
          const availableProjects = hasProjects
            ? session.availableProjects
            : state.availableProjects;
          const defaultProjectId = hasProjects
            ? session.defaultProjectId ?? null
            : state.defaultProjectId;
          return {
            user: session.user,
            accessToken: session.accessToken,
            expiresAt: session.expiresAt,
            availableProjects,
            defaultProjectId,
            activeProjectId: resolveActiveProjectId(
              state.activeProjectId,
              availableProjects,
              defaultProjectId,
            ),
          };
        });
      },
      setUser: (user) => {
        writeLastUser(user);
        set({ user });
      },
      updateTokens: (accessToken, expiresAt) => set({ accessToken, expiresAt }),
      clearSession: () =>
        set({
          user: null,
          accessToken: null,
          expiresAt: null,
          availableProjects: [],
          defaultProjectId: null,
          activeProjectId: null,
        }),
      setHydrated: () => set({ isHydrated: true }),
      setActiveProject: (projectId) =>
        set((state) =>
          state.availableProjects.some((p) => p.id === projectId)
            ? { activeProjectId: projectId }
            : {},
        ),
      resetActiveProject: () =>
        set((state) => ({
          activeProjectId: resolveActiveProjectId(
            null,
            state.availableProjects,
            state.defaultProjectId,
          ),
        })),
    }),
    {
      name: 'emlak-cms.auth',
      // K-8: partialize refresh token içermez — K-8 öncesi persist edilmiş
      // `refreshToken` alanı da bir sonraki yazımda otomatik temizlenir.
      // REQ-45: proje bağlamı da persist edilir (reload'da X-Project-Id korunur).
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        expiresAt: state.expiresAt,
        availableProjects: state.availableProjects,
        defaultProjectId: state.defaultProjectId,
        activeProjectId: state.activeProjectId,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // REQ-45 — persist edilen aktif proje artık erişilemiyorsa güvenli
        // projeye normalize et (kullanıcı erişimini yitirdiği projeye takılı kalmasın).
        const stillValid = state.availableProjects.some((p) => p.id === state.activeProjectId);
        if (!stillValid) state.resetActiveProject();
        state.setHydrated();
      },
    },
  ),
);
